import type {
  Author, Candidate, Column, StageStep, Stage, Comment, Override,
} from './types'
import { STAGES } from './types'

/* ─── Authors ─── */

export const ME: Author = { id: 'me', name: 'You',            initials: 'Y',  color: '#FD775E' }
const SJ: Author    = { id: 'sj', name: 'Sarah Jenkins',   initials: 'SJ', color: '#8B5CF6' }
const AF: Author    = { id: 'af', name: 'Alex Foster',     initials: 'AF', color: '#0969DA' }
const JP: Author    = { id: 'jp', name: 'Jordan Park',     initials: 'JP', color: '#1A7F37' }

/* ─── Stepper helper ─── */

function stepperFor(current: Stage): StageStep[] {
  const idx = STAGES.indexOf(current)
  const dates: Partial<Record<Stage, string>> = {
    applied: 'Mar 23', cv_screen: 'Mar 24', ai_interview: 'Mar 25',
    interview: 'Today', offer: undefined, hired: undefined,
  }
  return STAGES.map((s, i) => ({
    stage: s,
    state: i < idx ? 'done' : i === idx ? 'active' : 'upcoming',
    date: dates[s],
  }))
}

/* ─── Candidates ─── */

export const CANDIDATES: Candidate[] = [
  {
    id: 'kayla-morgan',
    name: 'Kayla Morgan',
    initials: 'KM',
    role: 'Investment Associate',
    company: 'Vanguard',
    location: 'Austin, TX',
    email: 'kayla.morgan@example.com',
    phone: '+1 512 555 0142',
    linkedinUrl: 'https://linkedin.com/in/kayla-morgan',
    avatarColor: '#0969DA',

    stage: 'interview',
    status: 'for_review',
    daysInStage: 0,

    overallScore: 82,
    headlineMatch: 'strong_match',
    sources: ['Referral'],

    ai: {
      headline: 'strong_match',
      summary:
        'Investment professional at Vanguard with 7 years in financial services. Strong quantitative background (M.S. Financial Engineering, UC Berkeley) with deep expertise in valuation, M&A analysis, and financial modelling. Well-aligned for the Finance Director role\'s analytical demands.',
      strengths: [
        'Deep financial engineering expertise',
        'Top-tier academic credentials (Berkeley)',
        'Strong tenure (7+ years at Vanguard)',
      ],
      growth: [
        'Limited people management evidence',
        'Relocation needed (Austin → New York)',
      ],
    },
    icp: [
      { id: 'icp-academic',    label: 'Academic Background',          level: 'very_strong', note: 'M.S. Financial Engineering, UC Berkeley' },
      { id: 'icp-finance-lead',label: 'Finance Leadership Experience', level: 'strong',     note: '7 years IC, informal team leadership' },
      { id: 'icp-industry',    label: 'Industry Knowledge',            level: 'moderate',   note: 'Investment side, less corporate finance' },
      { id: 'icp-mgmt',        label: 'People Management Scope',       level: 'moderate',   note: 'Mentor only, no direct reports' },
      { id: 'icp-location',    label: 'Location / Remote Fit',         level: 'weak',       note: 'Austin-based, NYC hybrid needed' },
    ],
    stepper: stepperFor('interview'),
    experience: [
      { id: 'e1', company: 'Vanguard',          role: 'Investment Associate',   start: 'Jun 2019', end: 'Present', summary: 'Modelling, valuation, equity analysis.' },
      { id: 'e2', company: 'Goldman Sachs',     role: 'Analyst, IB',            start: 'Jul 2017', end: 'May 2019', summary: 'M&A deal execution, financial modelling.' },
      { id: 'e3', company: 'UC Berkeley (Haas)',role: 'M.S. Financial Eng.',    start: 'Aug 2015', end: 'Jun 2017' },
    ],
    skills: ['Financial Modelling', 'Valuation', 'M&A', 'Python', 'Bloomberg', 'Capital IQ'],
    activity: [
      { id: 'a1', kind: 'override',     text: 'Override on "Industry knowledge" 5→7', at: '2h ago', by: 'Sarah Jenkins' },
      { id: 'a2', kind: 'stage_change', text: 'Moved to Interview',                   at: 'Today',  by: 'System' },
      { id: 'a3', kind: 'system',       text: 'AI Interview completed (22m 14s)',     at: 'Mar 25', by: 'Lantern AI' },
      { id: 'a4', kind: 'note',         text: 'Reached out re: relocation',           at: 'Mar 25', by: 'Alex Foster' },
    ],

    reports: [
      // CV Screen
      {
        kind: 'cv_screen',
        headlineScore: 85,
        requirementsMet: 5,
        requirementsTotal: 5,
        processedAt: 'Processed Mar 24',
        requirements: [
          { id: 'r1', label: '5+ years financial services',  passed: true,  note: '7 years at Vanguard + Goldman' },
          { id: 'r2', label: "Master's degree or equivalent", passed: true,  note: 'M.S. Financial Engineering, Berkeley' },
          { id: 'r3', label: 'Financial modelling experience', passed: true, note: 'Core of current role' },
          { id: 'r4', label: 'Valuation & M&A experience',     passed: true, note: 'Executed 4 M&A deals at Goldman' },
          { id: 'r5', label: 'Right to work in US',            passed: true },
        ],
        criteria: [
          {
            id: 'c1', label: 'Leadership & management', score: 6, maxScore: 10,
            note: 'Informal mentoring but no formal reports',
            overrides: [
              mkOverride('ov-1', SJ, '2d ago', 4, 6, 'Interview panel flagged stronger leadership signal than CV suggested — adjusting up.'),
            ],
            comments: [mkComment('c-1', SJ, '2d ago', 'Worth digging into during Interview — check the Goldman deal-lead stories.')],
          },
          {
            id: 'c2', label: 'Quantitative depth',      score: 10, maxScore: 10,
            note: 'MS Financial Engineering + 7yr quantitative work',
          },
          {
            id: 'c3', label: 'Industry-specific knowledge', score: 5, maxScore: 10,
            note: 'Strong in investment mgmt, less in corporate finance',
            overrides: [
              mkOverride('ov-2', SJ, '2h ago', 7, 5, 'Speaking with her yesterday — more corporate-finance exposure from Goldman than CV highlighted.'),
            ],
          },
          {
            id: 'c4', label: 'Location flexibility',       score: 4, maxScore: 10,
            note: 'Based in Austin, wants hybrid in NYC',
          },
        ],
      },
      // AI Interview
      {
        kind: 'ai_interview',
        headlineScore: 82,
        duration: '22m 14s',
        processedAt: 'Processed Mar 25',
        summary:
          'Confident on technical topics, clear in communication, showed strong structured thinking. Less certain on people-management scenarios.',
        qualitativeFit: {
          communication: 'very_strong',
          depth: 'strong',
          problemSolving: 'strong',
          culture: 'moderate',
        },
        competencies: [
          { id: 'ic1', label: 'Structured thinking',         score: 8, maxScore: 10, note: 'Clear frameworks, good recovery under push.' },
          { id: 'ic2', label: 'Technical depth',             score: 9, maxScore: 10, note: 'Comfortable with DCF, comps, precedent.' },
          { id: 'ic3', label: 'Communication clarity',       score: 9, maxScore: 10, note: 'Concise, checks assumptions.' },
          { id: 'ic4', label: 'Leadership situational judgment', score: 6, maxScore: 10, note: 'Good instincts, limited direct-report scenarios.' },
        ],
      },
      // Transcript (subset)
      {
        kind: 'transcript',
        duration: '22m 14s',
        turns: [
          { id: 't1', speaker: 'interviewer', at: '00:03', text: 'Walk me through a DCF you built recently for an acquisition target.' },
          { id: 't2', speaker: 'candidate',   at: '00:14', text: "Sure — at Vanguard I built a DCF for a regional bank target. I started by normalizing the 3-year historical, adjusted for the one-time restructuring charge in 2022, then projected cash flows out 5 years…" },
          { id: 't3', speaker: 'interviewer', at: '02:30', text: 'How did you handle the terminal value?' },
          { id: 't4', speaker: 'candidate',   at: '02:38', text: 'Gordon Growth with a 2% perpetuity rate, and I sanity-checked against exit-multiple method — came out within 4%.' },
          { id: 't5', speaker: 'interviewer', at: '06:12', text: 'Tell me about a time you led a team through a disagreement.' },
          { id: 't6', speaker: 'candidate',   at: '06:20', text: 'At Goldman I was on a deal team where the VP and analyst disagreed on the valuation floor…' },
        ],
      },
      // Interview (live — not yet completed)
      { kind: 'interview', scheduledAt: 'Today 4:30 PM', interviewer: AF, notes: 'Focus on people-management scenarios and NYC relocation.' },
    ],

    notes: [
      mkComment('n1', SJ, 'Mar 24', 'Strong CV. Worth advancing despite location gap — team is open to hybrid.'),
      mkComment('n2', AF, 'Mar 25', 'AI interview went well. Slight hesitation on leadership — worth probing live.'),
    ],

    files: [
      { id: 'f1', name: 'Kayla_Morgan_CV.pdf',          kind: 'pdf', size: '312 KB', uploadedAt: 'Mar 23' },
      { id: 'f2', name: 'Cover_Letter_FinanceDir.pdf',  kind: 'pdf', size: '98 KB',  uploadedAt: 'Mar 23' },
      { id: 'f3', name: 'Deal_Sheet_2019-2024.pdf',     kind: 'pdf', size: '1.2 MB', uploadedAt: 'Mar 23' },
    ],
  },

  // ─── Secondary cards for the kanban ───
  mkLite({ id: 'sarah-chen',    name: 'Sarah Chen',     initials: 'SC', role: 'Senior Frontend Engineer', company: 'Stripe',   color: '#E8624A', stage: 'cv_screen',    score: 92, match: 'strong_match', days: 1, strengths: ['5 yrs Stripe', 'TS migration lead'] }),
  mkLite({ id: 'james-wilson',  name: 'James Wilson',   initials: 'JW', role: 'Frontend Developer',       company: 'Shopify',  color: '#8B5CF6', stage: 'applied',      score: 72, match: 'match',         days: 2 }),
  mkLite({ id: 'maria-garcia',  name: 'Maria Garcia',   initials: 'MG', role: 'UI Engineer',              company: 'Figma',    color: '#1A7F37', stage: 'cv_screen',    score: 78, match: 'match',         days: 3 }),
  mkLite({ id: 'aisha-patel',   name: 'Aisha Patel',    initials: 'AP', role: 'Senior Engineer',          company: 'Linear',   color: '#0969DA', stage: 'ai_interview', score: 88, match: 'strong_match',  days: 3 }),
  mkLite({ id: 'elena-volkov',  name: 'Elena Volkov',   initials: 'EV', role: 'Staff Engineer',           company: 'GitHub',   color: '#CF222E', stage: 'interview',    score: 95, match: 'strong_match',  days: 2 }),
  mkLite({ id: 'alex-rivera',   name: 'Alex Rivera',    initials: 'AR', role: 'Senior Engineer',          company: 'Meta',     color: '#9A6700', stage: 'offer',        score: 91, match: 'strong_match',  days: 1 }),
  mkLite({ id: 'lucas-weber',   name: 'Lucas Weber',    initials: 'LW', role: 'Frontend Engineer',        company: 'Spotify',  color: '#E8624A', stage: 'applied',      score: 58, match: 'needs_review',  days: 5 }),
  mkLite({ id: 'hana-kim',      name: 'Hana Kim',       initials: 'HK', role: 'React Developer',          company: 'Coinbase', color: '#1A7F37', stage: 'applied',      score: 66, match: 'match',         days: 2 }),
  mkLite({ id: 'chris-martinez',name: 'Chris Martinez', initials: 'CM', role: 'Junior Developer',         company: 'Freelance',color: '#8B949E', stage: 'applied',      score: 38, match: 'not_a_fit',     days: 7 }),
]

/* ─── Builders ─── */

function mkComment(id: string, author: Author, at: string, text: string): Comment {
  return { id, author, at, text }
}
function mkOverride(id: string, author: Author, at: string, newValue: number | boolean, oldValue: number | boolean, reason: string): Override {
  return { id, author, at, newValue, oldValue, reason }
}

function mkLite(p: {
  id: string; name: string; initials: string; role: string; company: string
  color: string; stage: Stage; score: number
  match: import('./types').HeadlineMatch; days: number; strengths?: string[]
}): Candidate {
  return {
    id: p.id, name: p.name, initials: p.initials, role: p.role, company: p.company,
    avatarColor: p.color, stage: p.stage, status: 'in_progress', daysInStage: p.days,
    overallScore: p.score, headlineMatch: p.match, sources: ['Direct'],
    ai: {
      headline: p.match,
      summary: `${p.name.split(' ')[0]} brings a solid background at ${p.company}. Review the per-stage reports for details.`,
      strengths: p.strengths ?? ['Relevant experience', 'Clear communication'],
      growth: ['Needs deeper review on culture fit'],
    },
    icp: [
      { id: 'icp-1', label: 'Relevant experience',   level: p.score >= 85 ? 'very_strong' : p.score >= 70 ? 'strong' : 'moderate' },
      { id: 'icp-2', label: 'Technical depth',       level: p.score >= 85 ? 'very_strong' : p.score >= 70 ? 'strong' : 'moderate' },
      { id: 'icp-3', label: 'Communication',         level: p.score >= 80 ? 'strong' : 'moderate' },
      { id: 'icp-4', label: 'Culture signal',        level: 'moderate' },
    ],
    stepper: stepperFor(p.stage),
    skills: ['React', 'TypeScript', 'GraphQL'],
    activity: [
      { id: 'a1', kind: 'stage_change', text: `Moved to ${p.stage.replace('_', ' ')}`, at: `${p.days}d ago` },
    ],
    reports: [
      {
        kind: 'cv_screen',
        headlineScore: p.score,
        requirementsMet: p.score >= 70 ? 4 : p.score >= 50 ? 3 : 2,
        requirementsTotal: 5,
        processedAt: 'Processed recently',
        requirements: [
          { id: 'r1', label: '5+ years experience',    passed: p.score >= 60 },
          { id: 'r2', label: 'Framework proficiency',  passed: p.score >= 50 },
          { id: 'r3', label: 'Design-system work',     passed: p.score >= 70 },
          { id: 'r4', label: 'Startup exposure',       passed: p.score >= 85 },
          { id: 'r5', label: 'US / EU timezone',       passed: true },
        ],
        criteria: [
          { id: 'c1', label: 'Relevant experience', score: Math.round(p.score / 10), maxScore: 10 },
          { id: 'c2', label: 'Technical depth',     score: Math.round(p.score / 10), maxScore: 10 },
          { id: 'c3', label: 'Communication',       score: Math.round((p.score - 5) / 10), maxScore: 10 },
          { id: 'c4', label: 'Scope & impact',      score: Math.round((p.score - 10) / 10), maxScore: 10 },
        ],
      },
    ],
    notes: [], files: [],
  }
}

/* ─── Public accessors ─── */

export function getCandidates(): Candidate[] { return CANDIDATES }
export function getCandidate(id: string): Candidate | undefined {
  return CANDIDATES.find((c) => c.id === id)
}
export function getBoard(): Column[] {
  return STAGES.map<Column>((stage) => ({
    stage, title: stageTitle(stage),
    candidates: CANDIDATES.filter((c) => c.stage === stage),
  }))
}
function stageTitle(s: Stage): string {
  return { applied: 'Applied', cv_screen: 'CV Screen', ai_interview: 'AI Interview', interview: 'Interview', offer: 'Offer', hired: 'Hired' }[s]
}
